import React, { useState, useRef } from 'react';
import cx from 'classnames';

import Dialog from 'components/Dialog';
import Image from 'components/Image';
import Video from 'components/Video';
import { FileGQL } from 'types/typescript/file';
import './MultiImageFilePreviewModal.scss';
import Header from './Header';
import { Navigation } from './Navigation';

export interface Props {
  files: FileGQL[];
  selectedFile?: FileGQL;
  className?: string;
  onRequestHide?: () => void;
}

const MultiImageFilePreviewModal = ({ files, onRequestHide, selectedFile }: Props) => {
  const [currentFileInPreview, setCurrentFileInPreview] = useState<FileGQL | undefined>(
    selectedFile
  );
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  if (!currentFileInPreview) {
    return null;
  }

  const isVideo = Video.isSupportedType(currentFileInPreview.contentType);
  const isImage = Image.isSupportedType(currentFileInPreview.contentType);

  const { url, fullFileUrl, uuid, name } = currentFileInPreview;
  let selectedFileIndex = files.findIndex((f) => f.uuid === uuid);

  const buildLabel = () => {
    return selectedFileIndex + 1 + ' of ' + files.length;
  };

  const handleCloseButtonClick = () => {
    if (onRequestHide) {
      onRequestHide();
    }
  };

  const previewNext = (event: React.MouseEvent<any, MouseEvent>) => {
    event.stopPropagation();
    if (selectedFileIndex < files.length - 1) {
      setCurrentFileInPreview(files[selectedFileIndex + 1]);
    }
  };

  const previewPrevious = (event: React.MouseEvent<any, MouseEvent>) => {
    event.stopPropagation();
    if (selectedFileIndex > 0) {
      setCurrentFileInPreview(files[selectedFileIndex - 1]);
    }
  };

  const handleModalClick = (event: React.MouseEvent<any, MouseEvent>) => {
    if (onRequestHide) {
      const { target } = event;

      if (
        (headerRef &&
          headerRef?.current?.contains &&
          headerRef?.current?.contains(target as Node)) ||
        (contentRef &&
          contentRef?.current?.contains &&
          contentRef?.current?.contains(target as Node))
      ) {
        return;
      }
      onRequestHide();
    }
  };

  return (
    <Dialog
      box={false}
      hideCloseButton
      full
      theme="dark"
      className={cx('MultiImageFilePreviewModal')}
      onClick={handleModalClick}
    >
      <Header
        headerRef={headerRef}
        onCloseButtonClick={handleCloseButtonClick}
        url={fullFileUrl || url}
        name={name}
      />
      <div
        className="MultiImageFilePreviewModal-wrap"
        data-testid="multi-imagefile-preview-modal-wrap"
      >
        <div className="MultiImageFilePreviewModal-content" ref={contentRef}>
          {isImage && <Image className="MultiImageFilePreviewModal-image" src={url} key={uuid} />}
          {isVideo && <Video src={url} key={uuid} />}
        </div>
        <div className="MultiImageFilePreviewModal-controls">
          <Navigation
            onPreviousClick={previewPrevious}
            onNextClick={previewNext}
            label={buildLabel()}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default MultiImageFilePreviewModal;
